// Greenhold component prototype: <SealedDossier>. Story 3.4.
//
// Mounted at /prototype/sealed-dossier (no guard) per UX spec L444 —
// the team's MVP-Storybook substitute. Shows all 5 state-machine
// variants + the legacy preview mode for visual review:
//
//   1. All 5 states (idle / hover / pre-open / retired / selected) at md.
//   2. Legacy preview mode (Story 2.7 DossierShopCard path — title only).
//   3. Size sweep (thumb / md / lg / hero).
//   4. Interactive on/off (CSS :hover behavior).
//   5. Selected + interactive combo (vault-grid hover-over-selected).
//
// Mock fixtures use realistic creator-portfolio titles. One mock for
// the live/non-retired sections; a separate mock for the retired card
// so visitors see the foil overlay against a different name.

import { SealedDossier, type SealedDossierData } from "@greenhold/components";

const liveDossier: SealedDossierData = {
  id: "aaaaaaaa-1111-1111-1111-111111111111",
  drop_id: "dddddddd-1111-1111-1111-111111111111",
  edition_number: 87,
  portfolio_title: "Worlds 26 Showdown",
  drop_status: "live",
  retiring_at: null,
};

const retiredDossier: SealedDossierData = {
  id: "aaaaaaaa-2222-2222-2222-222222222222",
  drop_id: "dddddddd-2222-2222-2222-222222222222",
  edition_number: 1024,
  portfolio_title: "Holiday Heroes 2024",
  drop_status: "retired",
  retiring_at: "2025-01-08T00:00:00Z",
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm uppercase tracking-wider font-mono opacity-70 mb-3">
      {children}
    </h2>
  );
}

function StateCard({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-mono opacity-60 mb-2">{label}</p>
      {children}
    </div>
  );
}

export function SealedDossierPrototype() {
  return (
    <main className="min-h-screen bg-brand-paper-100 text-brand-forest-900 p-8">
      <div className="max-w-6xl mx-auto space-y-12">
        <header>
          <h1 className="text-3xl font-heading font-bold mb-2">
            &lt;SealedDossier&gt; prototype
          </h1>
          <p className="opacity-80">
            Story 3.4 — full 5-state machine extension of the Story 2.7
            v1-preview. Visual-review surface for the unopened-artifact
            states (UX-DR14 anatomy + epic AC L1363). Stories 3.6 /
            4.x / 5.x compose this component.
          </p>
        </header>

        {/* Section 1 — all 5 states, owned mode, size md. Extra
          padding around the pre-open card so its 1.4× scale doesn't
          clip into neighbors. */}
        <section>
          <SectionLabel>Section 1 — all 5 states · owned mode · size md</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-16 justify-items-start">
            <StateCard label='state="idle"'>
              <SealedDossier dossier={liveDossier} state="idle" />
            </StateCard>
            <StateCard label='state="hover"'>
              <SealedDossier dossier={liveDossier} state="hover" />
            </StateCard>
            <StateCard label='state="pre-open"'>
              <SealedDossier dossier={liveDossier} state="pre-open" />
            </StateCard>
            <StateCard label='state="retired"'>
              <SealedDossier dossier={retiredDossier} state="retired" />
            </StateCard>
            <StateCard label='state="selected"'>
              <SealedDossier dossier={liveDossier} state="selected" />
            </StateCard>
          </div>
        </section>

        {/* Section 2 — legacy preview mode (Story 2.7 path). No
          dossier prop → edition footer stays "Edition ____" lowercase. */}
        <section>
          <SectionLabel>
            Section 2 — legacy preview mode (Story 2.7 DossierShopCard
            path — title only, no edition number)
          </SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 justify-items-start">
            <StateCard label='title="Spring Showcase" · showRetirementWarning={false}'>
              <SealedDossier title="Spring Showcase" />
            </StateCard>
            <StateCard label='title="Holiday Heroes" · showRetirementWarning={true}'>
              <SealedDossier title="Holiday Heroes" showRetirementWarning />
            </StateCard>
          </div>
        </section>

        {/* Section 3 — size sweep. Same dossier + idle state across
          all 4 sizes. */}
        <section>
          <SectionLabel>Section 3 — size variants · thumb / md / lg / hero</SectionLabel>
          <div className="flex flex-wrap gap-8 items-start">
            <StateCard label="thumb">
              <SealedDossier dossier={liveDossier} size="thumb" />
            </StateCard>
            <StateCard label="md">
              <SealedDossier dossier={liveDossier} size="md" />
            </StateCard>
            <StateCard label="lg">
              <SealedDossier dossier={liveDossier} size="lg" />
            </StateCard>
            <StateCard label="hero">
              <SealedDossier dossier={liveDossier} size="hero" />
            </StateCard>
          </div>
        </section>

        {/* Section 4 — interactive on/off. Mouse over the second card
          to see CSS :hover lift + seal red-glow. */}
        <section>
          <SectionLabel>
            Section 4 — interactive prop (CSS :hover behavior — mouse over
            the second card)
          </SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 justify-items-start">
            <StateCard label="interactive={false}">
              <SealedDossier dossier={liveDossier} />
            </StateCard>
            <StateCard label="interactive={true} (mouse over)">
              <SealedDossier dossier={liveDossier} interactive />
            </StateCard>
          </div>
        </section>

        {/* Section 5 — selected + interactive combo. Selection ring
          + hover lift coexist when hovering. Demonstrates state
          orthogonality. */}
        <section>
          <SectionLabel>
            Section 5 — selected + interactive combo (vault-grid
            multi-select hover-over)
          </SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 justify-items-start">
            <StateCard label='state="selected" + interactive={true} (mouse over for combined effect)'>
              <SealedDossier dossier={liveDossier} state="selected" interactive />
            </StateCard>
          </div>
        </section>
      </div>
    </main>
  );
}
